import { useEffect, useMemo, useState } from "react";
import { BriefcaseBusiness, GraduationCap, Layers3 } from "lucide-react";
import { axiosReq } from "../../../commons/axiosReq";
import PricingBox from "./PricingBox";
import "./Pricing.css";
import { useLanguage } from "../../../i18n/LanguageContext";

const fallbackPlans = [
  { planId: 0, title: "شروع مسیر", description: "برای شناخت فرصت‌ها و ساخت یک مسیر اولیه و منظم", teacherNumber: 10, countryNumber: 3, planOptionTbls: [{ description: "ساخت پروفایل و مسیر شخصی" }, { description: "دسترسی به فرصت‌های منتخب" }] },
  { planId: 0, title: "جست‌وجوی حرفه‌ای", description: "برای اقدام هدفمند با ابزارهای کامل", teacherNumber: 40, countryNumber: 10, featured: true, planOptionTbls: [{ description: "ایمیل و درخواست شخصی‌سازی‌شده" }, { description: "پیگیری وضعیت درخواست‌ها" }, { description: "ابزارهای رزومه و مکاتبه" }] },
  { planId: 0, title: "همراهی کامل", description: "برای کسانی که می‌خواهند در تمام مسیر یک همراه داشته باشند", teacherNumber: 100, countryNumber: 20, planOptionTbls: [{ description: "راهنمایی برای انتخاب فرصت" }, { description: "بررسی مسیر و اولویت‌ها" }, { description: "همراهی برای پیدا کردن موقعیت" }] },
];

const scopes = [
  { id: "Study", label: "پلن‌های تحصیلی", description: "دانشگاه، استاد و اپلای تحصیلی", icon: GraduationCap },
  { id: "Job", label: "پلن‌های شغلی", description: "فرصت شغلی، رزومه و کارفرما", icon: BriefcaseBusiness },
  { id: "Both", label: "هر دو مسیر", description: "تحصیلی + شغلی با ۱۵٪ تخفیف", icon: Layers3 },
];

const Pricing = () => {
  const [plans, setPlans] = useState(fallbackPlans);
  const [scope, setScope] = useState("Study");
  const { isEnglish, direction } = useLanguage();
  const text = isEnglish ? { kicker: "A plan for every goal", title: "Separate plans for academic and career paths", desc: "Choose your goal first. Select the combined package if you need both paths and save 15%.", aria: "Path type", selection: { Study: ["Academic plans", "University, professor, and academic applications"], Job: ["Career plans", "International jobs, résumés, and employers"], Both: ["Both pathways", "Academic + career with 15% off"] }, discount: "15% off", both: "One subscription for simultaneous access to academic and career tools", study: "Plans designed for academic applications", job: "Plans designed for international job search", note: "The combined package includes both paths with a 15% discount." } : { kicker: "پلن مناسب برای هر هدف", title: "مسیر تحصیلی و شغلی، حالا کاملاً جدا", desc: "اول هدفت را انتخاب کن. اگر هر دو مسیر را می‌خواهی، بستهٔ ترکیبی را با ۱۵٪ تخفیف بگیر.", aria: "نوع مسیر", selection: { Study: ["پلن‌های تحصیلی", "دانشگاه، استاد و اپلای تحصیلی"], Job: ["پلن‌های شغلی", "فرصت شغلی، رزومه و کارفرما"], Both: ["هر دو مسیر", "تحصیلی + شغلی با ۱۵٪ تخفیف"] }, discount: "۱۵٪ تخفیف", both: "یک اشتراک برای دسترسی هم‌زمان به تمام ابزارهای مسیر تحصیلی و شغلی", study: "پلن‌های مخصوص اپلای تحصیلی", job: "پلن‌های مخصوص جست‌وجوی شغل بین‌المللی", note: "در بستهٔ ترکیبی، قیمت دو مسیر با ۱۵٪ تخفیف محاسبه می‌شود." };

  useEffect(() => {
    let active = true;
    const loadPlans = async () => {
      const result = await axiosReq("plan/GetHomePlans", "get");
      if (active && Array.isArray(result) && result.length) setPlans(result);
    };
    loadPlans();
    return () => { active = false; };
  }, []);

  const selectedScope = useMemo(() => scopes.find((item) => item.id === scope), [scope]);

  return (
    <section className="ia-pricing" id="pricing" dir={direction}>
      <div className="ia-container">
        <div className="ia-pricing__heading">
          <div><span>{text.kicker}</span><h2>{text.title}</h2></div>
          <p>{text.desc}</p>
        </div>
        <div className="ia-pricing__scope" role="tablist" aria-label={text.aria}>
          {scopes.map(({ id, icon: Icon }) => (
            <button key={id} type="button" role="tab" aria-selected={scope === id} className={scope === id ? "is-active" : ""} onClick={() => setScope(id)}>
              <span><Icon size={21} /></span><div><strong>{text.selection[id][0]}</strong><small>{text.selection[id][1]}</small></div>{id === "Both" && <b>{text.discount}</b>}
            </button>
          ))}
        </div>
        <div className="ia-pricing__selection"><span>{text.selection[selectedScope?.id]?.[0]}</span><p>{scope === "Both" ? text.both : scope === "Study" ? text.study : text.job}</p></div>
        <div className={`ia-pricing__grid ia-pricing__grid--${Math.min(plans.length, 3)}`}>
          {plans.slice(0, 3).map((plan, index) => <PricingBox key={`${plan.title}-${index}`} plan={plan} tierIndex={index} scope={scope} featured={Boolean(plan.featured) || (plans.length > 1 && index === 1)} />)}
        </div>
        <p className="ia-pricing__note">{text.note}</p>
      </div>
    </section>
  );
};

export default Pricing;
